import type { InterpolationEngine } from "../engine/InterpolationEngine";
import type { Color } from "../model/AtlasConfig";
import type { CompactShape } from "../model/realtime/CompactShape";
import type { CompactTimeline } from "../model/realtime/CompactTimeline";
import type { RealtimeCache } from "../model/RealtimeCache";
import type { Vehicle } from "../model/Vehicle";
import type { VehicleType } from "../model/VehicleType";
import type {
    StopAnchorWire,
    TripDelayWire,
    TripIndexEntry,
    TripScheduleWire,
} from "../types/dtos/realtime-api/realtime.dto";
import {
    COLLECTION_ENTRY_OVERHEAD_BYTES,
    NUMBER_BYTES,
    OBJECT_SLOT_BYTES,
    readJsHeapMetrics,
} from "./MemoryEstimate.tools";

/** Base overhead for a string object plus UTF-16 payload (bytes). */
function estimateStringBytes( value: string ): number {
    return OBJECT_SLOT_BYTES + value.length * 2;
}

/**
 * Estimates retained bytes for a compact shape buffer.
 *
 * @param shape Deduplicated compact shape instance.
 * @returns Approximate byte size.
 */
function estimateCompactShapeBytes( shape: CompactShape ): number {
    return OBJECT_SLOT_BYTES + shape.data.byteLength;
}

/**
 * Estimates retained bytes for a compact timeline.
 *
 * @param timeline Dense columnar timeline for one trip.
 * @returns Approximate byte size.
 */
function estimateCompactTimelineBytes( timeline: CompactTimeline ): number {
    return OBJECT_SLOT_BYTES +
      timeline.timestampS.byteLength +
      timeline.lat.byteLength +
      timeline.lon.byteLength +
      timeline.bearing.byteLength +
      timeline.stopSequence.byteLength +
      timeline.isStop.byteLength;
}

/**
 * Estimates retained bytes for stop anchors on a schedule.
 *
 * @param stops Stop list attached to a trip schedule.
 * @returns Approximate byte size.
 */
function estimateStopsBytes( stops: StopAnchorWire[] ): number {
    let total = OBJECT_SLOT_BYTES;
    for ( const stop of stops ) {
        total += COLLECTION_ENTRY_OVERHEAD_BYTES +
          NUMBER_BYTES * 4 +
          estimateStringBytes( stop.stop_id ) +
          estimateStringBytes( stop.name );
    }

    return total;
}

/**
 * Estimates retained bytes for one trip schedule payload.
 *
 * @param schedule Trip schedule wire object.
 * @returns Approximate byte size.
 */
function estimateTripScheduleBytes( schedule: TripScheduleWire ): number {
    return estimateStringBytes( schedule.trip_id ) +
      estimateStringBytes( schedule.shape_id ) +
      estimateStringBytes( schedule.route_short_name ) +
      estimateStringBytes( schedule.trip_headsign ) +
      NUMBER_BYTES * 5 +
      schedule.route_color.length * NUMBER_BYTES +
      estimateStopsBytes( schedule.stops );
}

/**
 * Estimates retained bytes for one trip index entry.
 *
 * @param entry Lightweight trip index row.
 * @returns Approximate byte size.
 */
function estimateTripIndexEntryBytes( entry: TripIndexEntry ): number {
    return estimateStringBytes( entry.trip_id ) +
      estimateStringBytes( entry.shape_id ) +
      estimateStringBytes( entry.route_short_name ) +
      NUMBER_BYTES * 3 +
      entry.route_color.length * NUMBER_BYTES +
      entry.bbox.length * NUMBER_BYTES;
}

/**
 * Estimates retained bytes for delay data.
 *
 * @param delay Delay payload for one trip.
 * @returns Approximate byte size.
 */
function estimateTripDelayBytes( delay: TripDelayWire ): number {
    return estimateStringBytes( delay.trip_id ) +
      NUMBER_BYTES +
      delay.stops.length * ( NUMBER_BYTES * 2 );
}

/**
 * Per-bucket memory metrics for {@link RealtimeCache}.
 */
export interface RealtimeCacheMemoryMetrics {
    "tripIndexCount":      number;
    "tripIndexBytes":      number;
    "shapesCount":         number;
    "shapePointsTotal":    number;
    "shapesBytes":         number;
    "schedulesCount":      number;
    "schedulesBytes":      number;
    "timelinesCount":      number;
    "timelinePointsTotal": number;
    "timelinesBytes":      number;
    "delaysCount":         number;
    "delaysBytes":         number;
    "knownShapeIdsCount":  number;
    "knownTripIdsCount":   number;
    "knownIdSetsBytes":    number;
    "totalBytes":          number;
}

/**
 * Memory metrics for {@link Vehicle} display metadata and small manager caches.
 * Trajectory bytes are counted under {@link RealtimeCacheMemoryMetrics.timelinesBytes} only.
 */
export interface RealtimeVehicleMemoryMetrics {
    "vehicleCount":         number;
    "vehicleMetadataBytes": number;
    "colorCacheCount":      number;
    "colorCacheBytes":      number;
    "typeCacheCount":       number;
    "typeCacheBytes":       number;
    "totalBytes":           number;
}

/**
 * Aggregated realtime memory report logged to the console.
 */
export interface RealtimeMemoryReport {
    "context":             string;
    "cache":               RealtimeCacheMemoryMetrics;
    "vehicles"?:           RealtimeVehicleMemoryMetrics;
    "interpolationStates": number;
    "interpolationBytes":  number;
    "jsHeapUsedBytes"?:    number;
    "jsHeapTotalBytes"?:   number;
    "estimatedTotalBytes": number;
}

/**
 * Measures approximate in-heap size of all {@link RealtimeCache} buckets.
 *
 * @param cache Active realtime cache instance.
 * @returns Per-bucket and total byte estimates.
 */
export function measureRealtimeCacheMemory( cache: RealtimeCache ): RealtimeCacheMemoryMetrics {
    let tripIndexBytes = 0;
    for ( const entry of cache.tripIndex.values() ) {
        tripIndexBytes += estimateTripIndexEntryBytes( entry );
    }

    let shapesBytes = 0;
    let shapePointsTotal = 0;
    for ( const entry of cache.shapes.values() ) {
        shapePointsTotal += entry.data.length;
        shapesBytes += estimateCompactShapeBytes( entry.data );
    }

    let schedulesBytes = 0;
    for ( const entry of cache.schedules.values() ) {
        schedulesBytes += estimateTripScheduleBytes( entry.data );
    }

    let timelinesBytes = 0;
    let timelinePointsTotal = 0;
    for ( const timeline of cache.timelines.values() ) {
        timelinePointsTotal += timeline.length;
        timelinesBytes += estimateCompactTimelineBytes( timeline );
    }

    let delaysBytes = 0;
    for ( const delay of cache.delays.values() ) {
        delaysBytes += estimateTripDelayBytes( delay );
    }

    let knownIdSetsBytes = 0;
    for ( const id of cache.knownShapeIds ) {
        knownIdSetsBytes += estimateStringBytes( id ) + COLLECTION_ENTRY_OVERHEAD_BYTES;
    }
    for ( const id of cache.knownTripIds ) {
        knownIdSetsBytes += estimateStringBytes( id ) + COLLECTION_ENTRY_OVERHEAD_BYTES;
    }

    const totalBytes = tripIndexBytes +
      shapesBytes +
      schedulesBytes +
      timelinesBytes +
      delaysBytes +
      knownIdSetsBytes;

    return {
        "tripIndexCount":     cache.tripIndex.size,
        tripIndexBytes,
        "shapesCount":        cache.shapes.size,
        shapePointsTotal,
        shapesBytes,
        "schedulesCount":     cache.schedules.size,
        schedulesBytes,
        "timelinesCount":     cache.timelines.size,
        timelinePointsTotal,
        timelinesBytes,
        "delaysCount":        cache.delays.size,
        delaysBytes,
        "knownShapeIdsCount": cache.knownShapeIds.size,
        "knownTripIdsCount":  cache.knownTripIds.size,
        knownIdSetsBytes,
        totalBytes,
    };
}

/**
 * Measures approximate memory used by {@link Vehicle} metadata and manager caches.
 *
 * @param vehicles Active vehicle map.
 * @param colorCache Route color string cache.
 * @param typeCache Route type cache.
 * @returns Vehicle-layer byte estimates.
 */
export function measureRealtimeVehicleMemory(
    vehicles: Map<string, Vehicle>,
    colorCache: Map<string, Color>,
    typeCache: Map<string, VehicleType>,
): RealtimeVehicleMemoryMetrics {
    let vehicleMetadataBytes = 0;

    for ( const vehicle of vehicles.values() ) {
        vehicleMetadataBytes += OBJECT_SLOT_BYTES +
          estimateStringBytes( vehicle.id ) +
          estimateStringBytes( vehicle.label ) +
          NUMBER_BYTES * 3;
    }

    let colorCacheBytes = 0;
    for ( const [ key ] of colorCache ) {
        colorCacheBytes += estimateStringBytes( key ) + NUMBER_BYTES * 3 + COLLECTION_ENTRY_OVERHEAD_BYTES;
    }

    let typeCacheBytes = 0;
    for ( const [ key ] of typeCache ) {
        typeCacheBytes += estimateStringBytes( key ) + COLLECTION_ENTRY_OVERHEAD_BYTES + OBJECT_SLOT_BYTES;
    }

    const totalBytes = vehicleMetadataBytes + colorCacheBytes + typeCacheBytes;

    return {
        "vehicleCount":    vehicles.size,
        vehicleMetadataBytes,
        "colorCacheCount": colorCache.size,
        colorCacheBytes,
        "typeCacheCount":  typeCache.size,
        typeCacheBytes,
        totalBytes,
    };
}

/**
 * Builds a full realtime memory report (cache + optional vehicle layer).
 *
 * @param params Report inputs.
 * @returns Structured memory breakdown.
 */
export function buildRealtimeMemoryReport( params: {
    "context":              string;
    "cache":                RealtimeCache;
    "vehicles"?:            Map<string, Vehicle>;
    "colorCache"?:          Map<string, Color>;
    "typeCache"?:           Map<string, VehicleType>;
    "interpolationEngine"?: InterpolationEngine;
} ): RealtimeMemoryReport {
    const cacheMetrics = measureRealtimeCacheMemory( params.cache );

    const vehicles = params.vehicles !== undefined &&
      params.colorCache !== undefined &&
      params.typeCache !== undefined
        ? measureRealtimeVehicleMemory( params.vehicles, params.colorCache, params.typeCache )
        : undefined;

    const interpolationStates = params.interpolationEngine?.getTrackedVehicleCount() ?? 0;
    const interpolationBytes = interpolationStates * (
        estimateStringBytes( "" ) + NUMBER_BYTES * 3 + COLLECTION_ENTRY_OVERHEAD_BYTES
    );

    const heap = readJsHeapMetrics();

    const estimatedTotalBytes = cacheMetrics.totalBytes +
      ( vehicles?.totalBytes ?? 0 ) +
      interpolationBytes;

    return {
        "context":          params.context,
        "cache":            cacheMetrics,
        vehicles,
        interpolationStates,
        interpolationBytes,
        "jsHeapUsedBytes":  heap.usedJSHeapSize,
        "jsHeapTotalBytes": heap.totalJSHeapSize,
        estimatedTotalBytes,
    };
}

/** Exported for unit tests. */
export const __testOnly = {
    estimateCompactShapeBytes,
    estimateCompactTimelineBytes,
};
